import React from 'react';
import './InstructionsPanel.css';

interface InstructionsPanelProps {
  onClose: () => void;
}

export const InstructionsPanel: React.FC<InstructionsPanelProps> = ({ onClose }) => {
  return (
    <div className="instructions-panel">
      <div className="instructions-header">
        <h2>How to Use</h2>
        <button className="close-button" onClick={onClose}>×</button>
      </div>

      <div className="instructions-content">
        <section className="instruction-section intro">
          <h3>🌊 Welcome to the Mussel Farm Digital Twin</h3>
          <p>
            This Mussel Farm Digital Twin is designed for <strong>measuring and predicting biodiversity 
            of artificial reefs in proximity to mussel farms</strong>. Use this tool to plan 
            reef placements, predict their ecological impact, and monitor biodiversity outcomes 
            in coastal marine environments.
          </p>
          <p>
            The system combines spatial planning with machine learning predictions to help you 
            understand how artificial reef deployments can enhance marine biodiversity near 
            aquaculture operations.
          </p>
        </section>

        <section className="instruction-section">
          <h3>📍 Creating Farms</h3>
          <ol>
            <li>Click <strong>"Create Farm"</strong> button</li>
            <li>Click on the map to place vertices of the farm boundary</li>
            <li>Complete the polygon by clicking near the first point</li>
            <li>Enter a name for your farm</li>
            <li>The farm will be created with default pods</li>
          </ol>
        </section>

        <section className="instruction-section">
          <h3>🪸 Placing Artificial Reefs</h3>
          <ol>
            <li>Click <strong>"Place Reef"</strong> button</li>
            <li>Click on the map to place a reef marker</li>
            <li>Enter reef details (name, volume, detection range)</li>
            <li>The reef will appear with a detection radius circle</li>
          </ol>
        </section>

        <section className="instruction-section">
          <h3>✏️ Editing Features</h3>
          <ul>
            <li><strong>Edit Farm:</strong> Select a farm, modify boundaries by dragging vertices</li>
            <li><strong>Edit Reef:</strong> Select a reef, drag to reposition, or edit details in popup</li>
            <li>Use the <strong>Management</strong> panel on the right to access edit controls</li>
          </ul>
        </section>

        <section className="instruction-section">
          <h3>🔬 Predicting Biodiversity</h3>
          <p><strong>For a Single Reef:</strong></p>
          <ol>
            <li>Click on a reef marker</li>
            <li>In the popup, click <strong>"Predict Biodiversity"</strong></li>
            <li>View predicted abundance and species richness</li>
          </ol>
          
          <p><strong>For an Area:</strong></p>
          <ol>
            <li>Click <strong>"Predict Biodiversity in Area"</strong> button</li>
            <li>Click on the map to define the area boundary</li>
            <li>Complete the polygon by clicking near the first point</li>
            <li>The system will count reefs in the area and predict biodiversity</li>
            <li>Results will appear in a notification and overlay box</li>
          </ol>
        </section>

        <section className="instruction-section">
          <h3>🗺️ Navigation</h3>
          <ul>
            <li>Use mouse wheel or +/- buttons to zoom</li>
            <li>Click and drag to pan the map</li>
            <li>Click <strong>"View Mode"</strong> to explore without editing</li>
            <li>Farms are clustered when zoomed out (below zoom level 14)</li>
          </ul>
        </section>

        <section className="instruction-section">
          <h3>📊 Management Panel</h3>
          <ul>
            <li>Access the right sidebar to view all farms and reefs</li>
            <li>Switch between <strong>Farms</strong> and <strong>Reefs</strong> tabs</li>
            <li>Click items to select and zoom to them</li>
            <li>Use Edit/Delete buttons for quick actions</li>
          </ul>
        </section>

        <section className="instruction-section tips">
          <h3>💡 Tips</h3>
          <ul>
            <li>Reef volume (in cm³) is used for biodiversity predictions</li>
            <li>Detection range shows the area monitored by each reef</li>
            <li>Purple polygons indicate area selection for biodiversity prediction</li>
            <li>Check reef surroundings to see network connections</li>
          </ul>
        </section>
      </div>
    </div>
  );
};
